import React from "react";

import { House, Info, NotListedLocation, PeopleAlt, PieChart, Place } from "@mui/icons-material";
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";

import { AudienceSummary } from "@/types";
import { abbreviateNumber } from "@/utils";
import { HelpBox } from "@/components/atoms";

interface AudienceSizeProps {
  data: AudienceSummary[];
}

const AudienceSegmentsSize: React.FC<AudienceSizeProps> = ({ data }) => {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    borderBottomColor: theme.palette.divider,
  }));

  const all_cols = data.some(x => x.population_count !== undefined) && data.some(x => x.invalid !== undefined)
  const invalid_cols = data.some(x => x.invalid !== undefined)

  const invalidText = `
    A site can be invalid either because it is not in the correct
    postcode format or because the postcode doesn't exist in our data.
  `

  return (
    <>
    <Grid container size={12}>
      <TableContainer component={Paper} sx={{ maxWidth: invalid_cols ? 750 : 600 }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <PieChart fontSize="small" color="secondary" />
                  <Typography variant="body2">Segment</Typography>
                </Stack>
              </StyledTableCell>
              <StyledTableCell align="right">
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <Place fontSize="small" color="secondary" />
                  <Typography variant="body2">
                    {data[0].num_postcodes ? "Postcodes" : "Sectors"}
                  </Typography>
                </Stack>
              </StyledTableCell>
              {data.some(x => x.num_households !== undefined) && (
                <StyledTableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <House fontSize="small" color="secondary" />
                    <Typography variant="body2">Households</Typography>
                  </Stack>
                </StyledTableCell>
              )}
              {data.some(x => x.population_count !== undefined) && (
                <StyledTableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <PeopleAlt fontSize="small" color="secondary" />
                    <Typography variant="body2">Audience Size</Typography>
                  </Stack>
                </StyledTableCell>
              )}
              {data.some(x => x.invalid !== undefined) && (
                <StyledTableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <NotListedLocation fontSize="small" color="secondary" />
                    <Typography variant="body2">Invalid Sites</Typography>
                    <HelpBox title={invalidText}>
                      <Info fontSize="small" color="info" />
                    </HelpBox>
                  </Stack>
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((segment, index) => (
              <TableRow
                key={`audience-summary-${index}`}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <StyledTableCell>
                  {segment.segment ?? `Segment ${index + 1}`}
                </StyledTableCell>
                {segment.num_postcodes !== undefined && (
                  <StyledTableCell align="right">
                    {`${abbreviateNumber(segment.num_postcodes)}${segment.estimate ? '*' : ''}`}
                  </StyledTableCell>
                )}
                {segment.num_sectors !== undefined && (
                  <StyledTableCell align="right">
                    {`${abbreviateNumber(segment.num_sectors)}${segment.estimate ? '*' : ''}`}
                  </StyledTableCell>
                )}
                {segment.num_households !== undefined && (
                  <StyledTableCell align="right">
                    {`${abbreviateNumber(segment.num_households)}${segment.estimate ? '*' : ''}`}
                  </StyledTableCell>
                )}
                {segment.population_count !== undefined && (
                  <StyledTableCell align="right">
                    {`${abbreviateNumber(segment.population_count)}${segment.estimate ? '*' : ''}`}
                  </StyledTableCell>
                )}
                {invalid_cols && (
                  <HelpBox title={segment.invalid ? segment.invalid.split(',').map(x => `"${x}"`).join(', ') : ''}>
                    <StyledTableCell align="right">
                      {abbreviateNumber(segment.invalid ? segment.invalid.split(',').length : 0)}
                    </StyledTableCell>
                  </HelpBox>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
      {data.some(x => x.estimate !== undefined) && (
        <Typography variant="caption" color="textSecondary">
          * These values are estimates and will be updated when the traveltime catchments are created.
        </Typography>
      )}
    </>
  );
};

export default AudienceSegmentsSize;
